"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { nativeLogout } from "@/lib/native-app";
import { cn } from "@/lib/utils";
import { BrandLogo } from "./brand-logo";
import { PrefsControls } from "./prefs-controls";
import { usePrefs } from "./prefs-provider";
import { Button, ButtonLink } from "./ui/button";
import { useMe } from "./use-me";

/**
 * Public-site header (landing, legal, auth). Logged-in users get a shortcut to the
 * dashboard; the dashboard itself uses <AppHeader/>.
 */
export function SiteHeader({ solid = false, minimal = false }: { solid?: boolean; minimal?: boolean }) {
  const pathname = usePathname();
  const router = useRouter();
  const { t } = usePrefs();
  const { user, setUser } = useMe([pathname]);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const opaque = solid || scrolled || menuOpen;

  async function logout() {
    setMenuOpen(false);
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    if (nativeLogout()) return;
    router.push("/");
    router.refresh();
  }

  const links = minimal ? [] : [{ href: "/#how", label: t.nav.how }];

  return (
    <header
      data-site-header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-200",
        opaque ? "border-b border-line bg-bg/95 backdrop-blur supports-[backdrop-filter]:bg-bg/80" : "border-b border-transparent bg-transparent",
      )}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <BrandLogo href="/" imageClassName="h-8 w-8" />

        {links.length > 0 ? (
          <nav aria-label={t.nav.mainNav} className="hidden items-center gap-1 md:flex">
            {links.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        ) : null}

        <div className="flex items-center gap-2">
          <PrefsControls compact className="hidden sm:flex" />

          <div className="hidden items-center gap-2 md:flex">
            {user ? (
              <>
                {pathname !== "/dashboard" ? (
                  <ButtonLink href="/dashboard" size="sm">
                    {t.nav.dashboard}
                  </ButtonLink>
                ) : null}
                <Button variant="ghost" size="sm" onClick={logout}>
                  {t.nav.logout}
                </Button>
              </>
            ) : (
              <>
                <ButtonLink href="/login" variant="ghost" size="sm">
                  {t.nav.login}
                </ButtonLink>
                <ButtonLink href="/register" native size="sm">
                  {t.nav.startFree}
                </ButtonLink>
              </>
            )}
          </div>

          {!user ? (
            <ButtonLink href="/register" native size="sm" className="md:hidden">
              {t.nav.startFree}
            </ButtonLink>
          ) : null}
          <Button
            variant="outline"
            size="icon-sm"
            className="md:hidden"
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            aria-label={menuOpen ? t.nav.closeMenu : t.nav.openMenu}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            id="site-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-line bg-bg md:hidden"
          >
            <div className="mx-auto max-w-6xl space-y-4 px-4 py-4">
              {links.length > 0 ? (
                <nav aria-label={t.nav.mainNav} className="flex flex-col">
                  {links.map((l) => (
                    <Link
                      key={l.href}
                      href={l.href}
                      onClick={() => setMenuOpen(false)}
                      className="rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-surface-2"
                    >
                      {l.label}
                    </Link>
                  ))}
                </nav>
              ) : null}
              <div className="flex items-center justify-between gap-3 border-t border-line pt-4 sm:hidden">
                <PrefsControls compact />
              </div>
              <div className="flex flex-col gap-2 border-t border-line pt-4">
                {user ? (
                  <>
                    <p className="truncate px-1 text-sm text-ink-2">{user.name}</p>
                    {pathname !== "/dashboard" ? (
                      <ButtonLink href="/dashboard" onClick={() => setMenuOpen(false)}>
                        {t.nav.dashboard}
                      </ButtonLink>
                    ) : null}
                    <Button variant="outline" onClick={logout}>
                      {t.nav.logout}
                    </Button>
                  </>
                ) : (
                  <>
                    <ButtonLink href="/register" native onClick={() => setMenuOpen(false)}>
                      {t.nav.startFree}
                    </ButtonLink>
                    <ButtonLink href="/login" variant="outline" onClick={() => setMenuOpen(false)}>
                      {t.nav.login}
                    </ButtonLink>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}

export function SiteFooter() {
  const { t } = usePrefs();
  const { user } = useMe();

  return (
    <footer className="border-t border-line bg-surface-2/60">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 sm:px-6 md:flex-row md:items-start md:justify-between">
        <div className="max-w-sm">
          <BrandLogo href="/" imageClassName="h-8 w-8" wordmarkAlways wordmarkClassName="text-lg" />
          <p className="mt-3 text-sm leading-relaxed text-ink-2">{t.footer.tagline}</p>
        </div>
        <nav aria-label={t.footer.links} className="grid grid-cols-2 gap-x-10 gap-y-2 text-sm sm:grid-cols-3">
          <Link href="/#pricing" className="py-1 text-ink-2 hover:text-ink">
            {t.nav.plans}
          </Link>
          <Link href="/privacy" className="py-1 text-ink-2 hover:text-ink">
            {t.footer.privacy}
          </Link>
          <Link href="/terms" className="py-1 text-ink-2 hover:text-ink">
            {t.footer.terms}
          </Link>
          <Link href="/accessibility" className="py-1 text-ink-2 hover:text-ink">
            {t.footer.accessibility}
          </Link>
          {user ? (
            <Link href="/dashboard" className="py-1 text-ink-2 hover:text-ink">
              {t.nav.dashboard}
            </Link>
          ) : (
            <Link href="/login" className="py-1 text-ink-2 hover:text-ink">
              {t.nav.login}
            </Link>
          )}
        </nav>
      </div>
    </footer>
  );
}
